<template>
  <div class="container">
    <div class="container01">
      <h1 class="container01_h1">销售开单</h1>

      <!-- 操作按钮 -->
      <el-row :gutter="20">
        <el-col :span="24" class="button-col">
          <div class="container01_button">
            <el-button @click="$router.back()">返回上一级</el-button>
            <el-button type="primary" @click="generateOrderNumber">生成单号</el-button>
            <el-button type="success" @click="saveOrder" :disabled="!canSave" :loading="saving">保存订单</el-button>
            <el-button type="warning" @click="printOrder" :disabled="!order.order_id">打印订单</el-button>
            <el-button @click="resetForm">重置</el-button>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 订单基本信息 -->
    <div class="order-info">
      <el-card header="订单信息">
        <el-form :model="order" label-width="100px">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item label="订单编号" required>
                <el-input v-model="order.order_number" placeholder="点击生成单号按钮自动生成"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="开单日期" required>
                <el-date-picker v-model="order.order_time" type="datetime" placeholder="选择开单日期"
                  value-format="YYYY-MM-DD HH:mm:ss" style="width: 100%">
                </el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="配送日期" required>
                <el-date-picker v-model="order.delivery_time" type="datetime" placeholder="选择配送日期"
                  value-format="YYYY-MM-DD HH:mm:ss" style="width: 100%">
                </el-date-picker>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="配送客户" required>
                <el-select v-model="order.customer_id" filterable placeholder="请选择客户" style="width: 100%"
                  @change="handleCustomerChange">
                  <el-option v-for="customer in customers" :key="customer.customer_id" :label="customer.name"
                    :value="customer.customer_id">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="订单状态">
                <el-select v-model="order.status" style="width: 100%">
                  <el-option label="正常" value="正常"></el-option>
                  <el-option label="已完成" value="已完成"></el-option>
                  <el-option label="已取消" value="已取消"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </el-card>
    </div>

    <!-- 商品明细 -->
    <div class="order-items">
      <el-card header="商品明细">
        <el-table :data="orderItems" border style="width: 100%">
          <el-table-column type="index" width="50" label="序号"></el-table-column>
          <el-table-column label="商品信息" min-width="200">
            <template #default="scope">
              <el-select 
                v-model="scope.row.merchandise_id" 
                filterable 
                placeholder="选择商品" 
                style="width: 100%"
                :ref="`merchandiseSelect_${scope.$index}`"
                @change="(value) => handleMerchandiseChange(value, scope.$index)"
                @keydown.enter="focusQuantityInput(scope.$index)">
                <el-option v-for="merchandise in merchandises" :key="merchandise.merchandise_id"
                  :label="`${merchandise.name} ${merchandise.specification || ''}`" :value="merchandise.merchandise_id">
                </el-option>
              </el-select>
            </template>
          </el-table-column>
          <el-table-column label="数量" width="200">
            <template #default="scope">
              <el-input-number 
                v-model="scope.row.quantity" 
                :min="0.01" 
                :step="1" 
                :precision="2"
                @change="calculateItemAmount(scope.$index)" 
                style="width: 100%"
                :ref="`quantityInput_${scope.$index}`"
                @keydown.enter="handleQuantityEnter(scope.$index)"
                @focus="handleQuantityFocus(scope.$index)">
              </el-input-number>
            </template>
          </el-table-column>
          <el-table-column label="单价" width="200">
            <template #default="scope">
              <el-input-number 
                v-model="scope.row.price" 
                :min="0" 
                :step="0.01" 
                :precision="2"
                @change="calculateItemAmount(scope.$index)" 
                style="width: 100%"
                :ref="`priceInput_${scope.$index}`"
                @keydown.enter="handlePriceEnter(scope.$index)"
                @focus="handlePriceFocus(scope.$index)">
              </el-input-number>
            </template>
          </el-table-column>
          <el-table-column label="金额" width="120">
            <template #default="scope">
              <span>{{ scope.row.amount.toFixed(2) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="80">
            <template #default="scope">
              <el-button type="danger" link @click="removeItem(scope.$index)" :disabled="orderItems.length <= 1">
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <div style="margin-top: 16px;">
          <el-button type="primary" @click="addItem">添加商品</el-button>
          <div style="float: right; text-align: right;">
            <div style="font-size: 16px; font-weight: bold;">
              合计金额: ¥ {{ totalAmount.toFixed(2) }}
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 打印区域（隐藏） -->
    <div v-if="order.order_id" id="printArea" style="display: none;">
      <div class="print-header">
        <h2>销售订单</h2>
        <p>订单编号: {{ order.order_number }}</p>
      </div>
      <div class="print-info">
        <p>客户: {{ selectedCustomerName }}</p>
        <p>开单时间: {{ order.order_time }}</p>
        <p>配送时间: {{ order.delivery_time }}</p>
      </div>
      <table class="print-table" border="1" style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th>商品名称</th>
            <th>规格</th>
            <th>数量</th>
            <th>单价</th>
            <th>金额</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in orderItems" :key="index">
            <td>{{ getMerchandiseName(item.merchandise_id) }}</td>
            <td>{{ getMerchandiseSpec(item.merchandise_id) }}</td>
            <td>{{ item.quantity }}</td>
            <td>{{ item.price.toFixed(2) }}</td>
            <td>{{ item.amount.toFixed(2) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="4" style="text-align: right;">合计:</td>
            <td>{{ totalAmount.toFixed(2) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import ordersAPI from '@/api/orders'
import customersAPI from '@/api/customers'
import merchandiseAPI from '@/api/merchandises'

export default {
  name: 'CreateOrderView',
  data() {
    return {
      order: {
        order_number: '',
        customer_id: null,
        order_time: '',
        delivery_time: '',
        status: '正常',
        order_id: null,
        isEditMode: false,
        editingOrderId: null
      },
      orderItems: [
        {
          merchandise_id: null,
          quantity: 1,
          price: 0,
          amount: 0
        }
      ],
      customers: [],
      merchandises: [],
      loading: false,
      saving: false
    }
  },
  computed: {
    totalAmount() {
      return this.orderItems.reduce((sum, item) => sum + item.amount, 0)
    },
    canSave() {
      return this.order.order_number &&
        this.order.customer_id &&
        this.order.order_time &&
        this.order.delivery_time &&
        this.orderItems.every(item => item.merchandise_id && item.quantity > 0)
    },
    selectedCustomerName() {
      const customer = this.customers.find(c => c.customer_id === this.order.customer_id)
      return customer ? customer.name : ''
    }
  },
  created() {
    this.fetchCustomers()
    this.fetchMerchandises()
    this.setDefaultDates()
    const isEdit = this.$route.query.edit === 'true'
    const orderId = this.$route.query.order_id
    if (isEdit && orderId) {
      this.loadOrderData(parseInt(orderId))
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.focusMerchandiseSelect(0)
    })
  },
  methods: {
    setDefaultDates() {
      const now = new Date()
      this.order.order_time = now.toISOString().slice(0, 19).replace('T', ' ')
      const tomorrow = new Date(now)
      tomorrow.setDate(tomorrow.getDate() + 1)
      this.order.delivery_time = tomorrow.toISOString().slice(0, 19).replace('T', ' ')
    },
    async fetchCustomers() {
      try {
        const response = await customersAPI.inquireCustomers({ per_page: 1000 })
        this.customers = response.customers || []
      } catch (error) {
        console.error('获取客户列表失败:', error)
        ElMessage.error('获取客户列表失败')
      }
    },
    async fetchMerchandises() {
      try {
        const response = await merchandiseAPI.inquireMerchandises({ per_page: 1000 })
        this.merchandises = response.merchandises || []
      } catch (error) {
        console.error('获取商品列表失败:', error)
        ElMessage.error('获取商品列表失败')
      }
    },
    async generateOrderNumber() {
      try {
        const response = await ordersAPI.generateOrderNumber()
        this.order.order_number = response.order_number
        ElMessage.success('订单编号生成成功')
      } catch (error) {
        console.error('生成订单编号失败:', error)
        ElMessage.error('生成订单编号失败')
      }
    },
    async handleMerchandiseChange(merchandiseId, index) {
      if (this.order.customer_id && merchandiseId) {
        await this.loadHistoricalPrice(this.order.customer_id, merchandiseId, index)
      }
      this.calculateItemAmount(index)
      this.$nextTick(() => {
        this.focusQuantityInput(index)
      })
    },
    focusMerchandiseSelect(index) {
      const ref = `merchandiseSelect_${index}`
      if (this.$refs[ref] && this.$refs[ref].focus) {
        this.$refs[ref].focus()
      }
    },
    focusQuantityInput(index) {
      const ref = `quantityInput_${index}`
      if (this.$refs[ref] && this.$refs[ref].focus) {
        this.$refs[ref].focus()
        this.$nextTick(() => {
          this.selectInputText(ref)
        })
      }
    },
    focusPriceInput(index) {
      const ref = `priceInput_${index}`
      if (this.$refs[ref] && this.$refs[ref].focus) {
        this.$refs[ref].focus()
        this.$nextTick(() => {
          this.selectInputText(ref)
        })
      }
    },
    handleQuantityFocus(index) {
      const ref = `quantityInput_${index}`
      this.$nextTick(() => {
        this.selectInputText(ref)
      })
    },
    handlePriceFocus(index) {
      const ref = `priceInput_${index}`
      this.$nextTick(() => {
        this.selectInputText(ref)
      })
    },
    selectInputText(refName) {
      const inputComponent = this.$refs[refName]
      if (inputComponent && inputComponent.$el) {
        const inputEl = inputComponent.$el.querySelector('input')
        if (inputEl && inputEl.select) {
          inputEl.select()
        }
      }
    },
    handleQuantityEnter(index) {
      this.$nextTick(() => {
        this.focusPriceInput(index)
      })
    },
    handlePriceEnter(index) {
      if (index === this.orderItems.length - 1) {
        this.addItem()
      } else {
        this.$nextTick(() => {
          this.focusMerchandiseSelect(index + 1)
        })
      }
    },
    handleCustomerChange(customerId) {
      this.orderItems.forEach(async (item, index) => {
        if (item.merchandise_id) {
          await this.loadHistoricalPrice(customerId, item.merchandise_id, index)
        }
      })
    },
    async loadHistoricalPrice(customerId, merchandiseId, index) {
      try {
        const historicalPrice = await this.getHistoricalPrice(customerId, merchandiseId)
        if (historicalPrice !== null && historicalPrice !== undefined) {
          this.orderItems[index].price = historicalPrice
          ElMessage.success(`已自动匹配历史价格: ${historicalPrice}`)
        } else {
          await this.loadCustomerPrice(customerId, merchandiseId, index)
        }
      } catch (error) {
        console.error('加载价格失败:', error)
        await this.loadCustomerPrice(customerId, merchandiseId, index)
      }
    },
    async getHistoricalPrice(customerId, merchandiseId) {
      try {
        const response = await ordersAPI.getCustomerMerchandisePrice(customerId, merchandiseId)
        return response.price || null
      } catch (error) {
        console.error('获取历史价格失败:', error)
        return null
      }
    },
    async loadCustomerPrice(customerId, merchandiseId, index) {
      try {
        const response = await ordersAPI.getCustomerMerchandisePrice(customerId, merchandiseId)
        if (response.price !== undefined && response.price !== null) {
          this.orderItems[index].price = response.price
          this.calculateItemAmount(index)
        } else {
          this.orderItems[index].price = 0
          this.calculateItemAmount(index)
        }
      } catch (error) {
        console.error('获取价格失败:', error)
        this.orderItems[index].price = 0
        this.calculateItemAmount(index)
      }
    },
    calculateItemAmount(index) {
      const item = this.orderItems[index]
      item.amount = item.quantity * item.price
    },
    addItem() {
      this.orderItems.push({
        merchandise_id: null,
        quantity: 1,
        price: 0,
        amount: 0
      })
      this.$nextTick(() => {
        const newIndex = this.orderItems.length - 1
        this.focusMerchandiseSelect(newIndex)
      })
    },
    removeItem(index) {
      if (this.orderItems.length > 1) {
        this.orderItems.splice(index, 1)
        this.$nextTick(() => {
          if (index > 0) {
            this.focusPriceInput(index - 1)
          } else {
            this.focusMerchandiseSelect(0)
          }
        })
      }
    },
    async saveOrder() {
      try {
        this.saving = true
        const orderData = {
          order: {
            order_number: this.order.order_number,
            customer_id: this.order.customer_id,
            order_time: this.order.order_time,
            delivery_time: this.order.delivery_time,
            status: this.order.status
          },
          items: this.orderItems.map(item => ({
            merchandise_id: item.merchandise_id,
            quantity: item.quantity,
            price: item.price
          }))
        }
        let response
        if (this.isEditMode) {
          response = await ordersAPI.updateOrder(this.editingOrderId, orderData)
          this.order.order_id = this.editingOrderId
        } else {
          response = await ordersAPI.createOrder(orderData)
          this.order.order_id = response.order_id
        }
        ElMessage.success(this.isEditMode ? '订单更新成功' : '订单保存成功')
      } catch (error) {
        console.error('保存订单失败:', error)
        ElMessage.error(error.response?.data?.message || '保存订单失败')
      } finally {
        this.saving = false
      }
    },
    async loadOrderData(orderId) {
      try {
        this.loading = true
        const response = await ordersAPI.inquireOrder(orderId)
        this.order = {
          order_number: response.order_number,
          customer_id: response.customer_id,
          order_time: response.order_time,
          delivery_time: response.delivery_time,
          status: response.status,
          order_id: response.order_id
        }
        this.orderItems = response.items.map(item => ({
          merchandise_id: item.merchandise_id,
          quantity: item.quantity,
          price: item.price,
          amount: item.amount
        }))
        this.isEditMode = true
        this.editingOrderId = orderId
      } catch (error) {
        console.error('加载订单数据失败:', error)
        ElMessage.error('加载订单数据失败')
      } finally {
        this.loading = false
      }
    },
    printOrder() {
      const printContent = document.getElementById('printArea').innerHTML
      const originalTitle = document.title
      document.title = '销售订单-' + this.order.order_number
      const printWindow = window.open('', '_blank')
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${document.title}</title>
            <style>
              body { 
                font-family: 'SimSun', serif; 
                margin: 20px;
                font-size: 14px;
              }
              .print-header {
                text-align: center;
                margin-bottom: 20px;
                border-bottom: 2px solid #000;
                padding-bottom: 10px;
              }
              .print-header h2 {
                margin: 0;
                font-size: 24px;
              }
              .print-info p {
                margin: 5px 0;
                display: inline-block;
                margin-right: 30px;
              }
              .print-table {
                margin-top: 20px;
                font-size: 14px;
                width: 100%;
                border-collapse: collapse;
              }
              .print-table th,
              .print-table td {
                padding: 8px;
                text-align: center;
                border: 1px solid #000;
              }
              .print-table th {
                background-color: #f5f5f5;
                font-weight: bold;
              }
              @media print {
                body { margin: 0; }
              }
            </style>
          </head>
          <body>${printContent}</body>
        </html>
      `)
      printWindow.document.close()
      printWindow.onload = () => {
        printWindow.focus()
        printWindow.print()
        printWindow.onafterprint = () => {
          printWindow.close()
          document.title = originalTitle
        }
      }
    },
    resetForm() {
      this.order = {
        order_number: '',
        customer_id: null,
        order_time: '',
        delivery_time: '',
        status: '正常',
        order_id: null
      }
      this.orderItems = [{
        merchandise_id: null,
        quantity: 1,
        price: 0,
        amount: 0
      }]
      this.setDefaultDates()
      this.$nextTick(() => {
        this.focusMerchandiseSelect(0)
      })
    },
    getMerchandiseName(merchandiseId) {
      const merchandise = this.merchandises.find(m => m.merchandise_id === merchandiseId)
      return merchandise ? merchandise.name : ''
    },
    getMerchandiseSpec(merchandiseId) {
      const merchandise = this.merchandises.find(m => m.merchandise_id === merchandiseId)
      return merchandise ? merchandise.specification : ''
    }
  }
}
</script>

<style scoped>
.container {
  padding: 20px;
  animation: fadeIn 0.3s ease;
}
.container01_h1 {
  text-align: center;
  margin-bottom: 24px;
  font-size: 28px;
  font-weight: 600;
  background: linear-gradient(135deg, #1e293b, #3b82f6);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.container01_button {
  text-align: center;
  margin-bottom: 24px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
}
.order-info,
.order-items {
  margin-bottom: 24px;
}
.el-table__row:hover {
  background-color: #fef9e3 !important;
  cursor: pointer;
}
.order-items .el-card__body > div:last-child {
  background: linear-gradient(135deg, #f8fafc, #ffffff);
  padding: 16px;
  border-radius: 16px;
  margin-top: 16px;
  border: 1px solid #eef2ff;
}
.order-items .el-card__body > div:last-child div:last-child {
  font-size: 18px;
  color: var(--danger-color);
  animation: pulse 2s infinite;
}
@keyframes pulse {
  0% { opacity: 0.8; }
  50% { opacity: 1; text-shadow: 0 0 2px rgba(239, 68, 68, 0.3); }
  100% { opacity: 0.8; }
}
.el-input-number .el-input__inner:focus,
.el-input .el-input__inner:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
}
@media (max-width: 768px) {
  .container {
    padding: 12px !important;
  }
  .container01_h1 {
    font-size: 20px;
    margin: 12px 0;
  }
  .container01_button {
    flex-direction: column;
    gap: 8px;
  }
  .el-button {
    width: 100%;
    margin: 0 !important;
  }
  .order-info .el-form-item {
    margin-bottom: 16px;
  }
  .order-info .el-col {
    width: 100% !important;
  }
  .el-table {
    font-size: 12px;
  }
  .el-table th,
  .el-table td {
    padding: 6px 4px;
  }
  .el-input-number {
    width: 100% !important;
  }
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>